import styled from '@emotion/styled';
import { Dropdown, Image, Space } from 'antd';
import { ReactNode, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { FaOutdent } from 'react-icons/fa';
import { FiGlobe, FiLogOut, FiMoon, FiSun } from 'react-icons/fi';
import { MdAccountCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ConnectedUserEntity,
  ProfilType,
} from '../../../entities/ConnectedUserEntity';
import { createForums, createUser } from '../../../redux/userStore/actions';
import { ROUTES } from '../../../routes';
import { ORANGE, SECONDARY, YELLOW } from '../../../shared/colors';
import { AppLanguage, useAppearance } from '../../../shared/appearance';
import { defaultImage } from '../../../shared/defaultImage';
import { checkProfile, ProtectedView } from '../ProtectedRoutes';
import { SUB_MENUS } from './Menus';

const NavLink = styled.div`
  font-size: 12.5px;
  cursor: pointer;
  color: var(--mt-header-text);
  font-family: Poppins-R;
  margin: 0px;
  position: relative;
  opacity: 0.74;
  padding: 8px 10px;
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    text-decoration: none;
    color: ${YELLOW};
    opacity: 1;
  }

  &.active {
    color: ${YELLOW};
    opacity: 1;
    font-weight: bold;
    background: rgba(255, 176, 0, 0.08);
    box-shadow: inset 0 -2px 0 ${YELLOW};
  }

  @media (max-width: 767px) {
    font-size: 17px;
    padding: 13px 0;
    width: fit-content;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  background:
    linear-gradient(90deg, rgba(255, 79, 31, 0.1), transparent 28%),
    ${SECONDARY};
  color: var(--mt-header-text) !important;
  padding: 6px 18px;
  min-height: 56px;
  border-bottom: 1px solid var(--mt-header-border);
  box-shadow: 0 12px 30px rgba(8, 13, 21, 0.22);

  .connected {
    display: flex;
    align-items: center;
    margin: 0px;
    padding: 2px;
    border: 1px solid var(--mt-header-border);
    background: rgba(255, 255, 255, 0.08);
    font-family: Poppins-R;
    font-size: 10px;
    border-radius: 20px;
    min-width: 140px;
  }

  > .logo {
    flex: 1.6;
    gap: 9px;
  }

  .brand-title {
    color: var(--mt-header-text);
    font-family: Poppins-B;
    font-size: 22px;
    margin-bottom: 0;
    letter-spacing: 0;
  }

  > .linkContainer {
    display: none;
  }

  > .menu-button {
    display: block;
    cursor: pointer;
    margin-right: 10px;
  }

  @media (min-width: 768px) {
    > .linkContainer {
      display: flex;
      flex: 8;
      justify-content: flex-end;
      align-items: center;
      gap: 6px;
      padding-left: 0px;

      > * {
        margin-right: 0;
      }
    }

    > .menu-button {
      display: none;
    }
  }

  @media (min-width: 1200px) {
    > .linkContainer {
      flex: 8;
      // padding-left: 20%;
    }
  }

  .menu {
    height: 125vh;
    width: 100%;
    position: fixed;
    z-index: 1000;
    top: 0;
    left: 0;
    background-color: ${SECONDARY};
    overflow-y: auto;
    overflow-x: hidden;
    transition: 0.5s;
    padding: 28px 30px 40px;
  }

  .menu-close {
    height: 0px;
    width: 100%;
    position: fixed;
    z-index: 1000;
    top: 0;
    left: 0;
    background-color: ${SECONDARY};
    overflow-x: hidden;
    transition: 0.5s;
  }

  .menu-linkContainer {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 24px;
  }

  .logout {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-family: Poppins-R;
    font-size: 12px;
    height: 30px;
    padding: 0 10px;
    border: 1px solid var(--mt-header-border);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.065);
    color: rgba(248, 250, 252, 0.84);
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

    &:hover {
      background: rgba(217, 45, 32, 0.12);
      border-color: rgba(217, 45, 32, 0.48);
      color: #ffffff;
    }
  }

  .appearance-controls {
    align-items: center;
    display: flex;
    gap: 8px;
    margin-left: 8px;
  }

  .theme-switch,
  .language-switch {
    align-items: center;
    background: rgba(255, 255, 255, 0.065);
    border: 1px solid var(--mt-header-border);
    border-radius: 999px;
    color: var(--mt-header-text);
    display: inline-flex;
    height: 30px;
  }

  .theme-switch {
    cursor: pointer;
    gap: 6px;
    padding: 0 10px;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }

  .theme-switch:hover,
  .language-switch:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 176, 0, 0.46);
  }

  .theme-switch span {
    font-family: Poppins-R;
    font-size: 12px;
  }

  .language-switch {
    gap: 2px;
    padding: 3px;
  }

  .language-switch .globe {
    color: ${YELLOW};
    margin: 0 4px 0 6px;
  }

  .language-switch button {
    background: transparent;
    border: 0;
    border-radius: 999px;
    color: rgba(248, 250, 252, 0.64);
    cursor: pointer;
    font-family: Poppins-B;
    font-size: 11px;
    height: 22px;
    min-width: 30px;
    padding: 0 7px;
  }

  .language-switch button.active {
    background: ${YELLOW};
    color: #041018;
  }

  @media (max-width: 767px) {
    padding-right: 18px;

    > .logo {
      min-width: 0;
    }

    .brand-title {
      font-size: 23px;
    }

    .connected {
      border-radius: 14px;
      font-size: 12px;
      margin-top: 12px;
      max-width: 240px;
      padding: 8px 10px;
    }

    .logout {
      align-items: flex-start;
      border-top: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: 0;
      flex-direction: row;
      gap: 8px;
      margin-top: 20px;
      padding: 18px 0 0;
      width: 100%;
    }

    .appearance-controls {
      align-items: flex-start;
      border-top: 1px solid var(--mt-header-border);
      flex-direction: column;
      margin-top: 18px;
      padding-top: 18px;
    }
  }
`;

const SubHeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${SECONDARY};
  gap: 8px;
  padding: 5px 44px 5px 16px;
  min-height: 34px;
  border-top: 1px solid var(--mt-header-border);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .sub-link {
    color: var(--mt-header-text);
    cursor: pointer;
    font-family: Poppins-R;
    opacity: 0.78;
    padding: 5px 9px;
    border-radius: 5px;
    white-space: nowrap;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: ${YELLOW};
      opacity: 1;
    }

    &.active {
      font-weight: bold;
      color: ${YELLOW};
      opacity: 1;
      box-shadow: inset 0 -2px 0 ${YELLOW};
    }
  }

  @media (max-width: 767px) {
    justify-content: flex-start;
    overflow-x: auto;
    padding: 10px 16px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .sub-link {
      font-size: 13px;
    }
  }
`;

// Ajout pour rendre le menu principal actif quand un sous-menu est actif
const isMainMenuActive = (route: string): boolean => {
  // eslint-disable-next-line no-restricted-globals
  if (location.pathname === route) return true;
  const subMenus = SUB_MENUS[route];
  if (!subMenus) return false;
  return subMenus.some((item) => {
    // eslint-disable-next-line no-restricted-globals
    if (item.path && location.pathname.startsWith(item.path)) return true;
    if (item.dropdown) {
      return item.children.some((child) =>
        // eslint-disable-next-line no-restricted-globals
        location.pathname.startsWith(child.path),
      );
    }
    return false;
  });
};
const MenuContent = ({
  closeMenu,
  other,
}: {
  closeMenu?: () => void;
  other?: ReactNode;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { language, setLanguage, setTheme, t, theme } = useAppearance();

  const connectedUser: ConnectedUserEntity | null =
    useSelector((state: any) => state?.userReducer?.user) ?? null;

  const dispatch = useDispatch();

  const isActive = (path: string) => location.pathname === path;

  // Helper (peut être dans un utils/acl.ts)
  const hasAnyRole = (profils: any, roles: ProfilType[]) =>
    roles.some((r) => checkProfile(profils, r));

  // Dans ton composant :
  const canSeeMenus =
    !!connectedUser?.authentifie &&
    hasAnyRole(connectedUser?.profils, [
      'resp_commercial',
      'commercial',
      'chef_agence',
      'responsable',
      'gestionnaire',
    ]);

  return (
    <div className={closeMenu ? 'menu-linkContainer' : 'linkContainer'}>
      <NavLink
        className={isActive(ROUTES.HOME_PAGE) ? 'active' : ''}
        onClick={() => {
          closeMenu?.();
          history.push(ROUTES.HOME_PAGE);
        }}
      >
        {t('home')}
      </NavLink>

      <NavLink
        className={isMainMenuActive(ROUTES.FORMATION.PAGE) ? 'active' : ''}
        onClick={() => {
          closeMenu?.();
          history.push(ROUTES.FORMATION.PAGE);
        }}
      >
        {t('fichesInfos')}
      </NavLink>

      <ProtectedView
        authorization={[
          'resp_commercial',
          'commercial',
          'chef_agence',
          'gestionnaire',
          'admin',
          'sadmin',
        ]}
      >
        <NavLink
          className={isActive(ROUTES.TCHAT.PAGE) ? 'active' : ''}
          onClick={() => {
            closeMenu?.();
            history.push(ROUTES.TCHAT.PAGE);
          }}
        >
          {t('chat')}
        </NavLink>
      </ProtectedView>
      {/* ajoute le menu admin */}
      {connectedUser?.authentifie &&
      checkProfile(connectedUser.profils, 'admin') ? (
        <NavLink
          className={
            isMainMenuActive(ROUTES.PANEL.ADMIN.PARTENAIRE) ? 'active' : ''
          }
          onClick={() => {
            closeMenu?.();
            history.push(ROUTES.PANEL.ADMIN.PARTENAIRE);
          }}
        >
          {t('administration')}
        </NavLink>
      ) : (
        <></>
      )}
      {connectedUser?.authentifie &&
        checkProfile(connectedUser?.profils, 'sadmin') && (
          <NavLink
            className={isMainMenuActive(ROUTES.ADMIN.CONSOLE) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.ADMIN.CONSOLE);
            }}
          >
            {t('administration')}
          </NavLink>
        )}

      {/* ajoute le menu sadmin */}
      {canSeeMenus && (
        <>
          <NavLink
            className={
              isMainMenuActive(ROUTES.PANEL.CLIENTELE.CLIENTS) ? 'active' : ''
            }
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.PANEL.CLIENTELE.CLIENTS);
            }}
          >
            {t('clientPortfolio')}
          </NavLink>

          <NavLink
            className={
              isMainMenuActive(ROUTES.PANEL.EMISSION.TITRE) ? 'active' : ''
            }
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.PANEL.EMISSION.TITRE);
            }}
          >
            {t('titlePortfolio')}
          </NavLink>

          <NavLink
            className={isMainMenuActive(ROUTES.DASHBOARD.PAGE) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.DASHBOARD.PAGE);
            }}
          >
            {t('transactions')}
          </NavLink>
        </>
      )}

      {connectedUser?.authentifie &&
      new Date().getTime() < connectedUser.nextAuthDate ? (
        <>
          <NavLink
            className={isActive(ROUTES.DASHBOARD.PROFIL_KYC) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.DASHBOARD.PROFIL_KYC);
            }}
          >
            <div className='connected'>
              <MdAccountCircle size={30} />
              <span>
                {t('welcome')} <br />{' '}
                <span style={{ fontSize: 12 }}>
                  {connectedUser.nom} {connectedUser.prenom}
                </span>
              </span>
            </div>
          </NavLink>

          <div
            className='logout'
            onClick={() => {
              dispatch(
                createUser({
                  authentifie: false,
                  id: '',
                  url: '',
                  nom: '',
                  prenom: '',
                  profils: [],
                  manager: { nom: '', code: '', url: '', code_referent: '' },
                  partenaire: { nom: '', url: '', code_referent: '' },
                  email: '',
                  token: '',
                  nextAuthDate: 0,
                  achat: 0,
                  vente: 0,
                }),
              );
              dispatch(createForums([]));
              history.push(ROUTES.HOME_PAGE);
            }}
          >
            <FiLogOut />
            {t('logout')}
          </div>
        </>
      ) : (
        <>
          <NavLink onClick={() => {}}>
            {/* <Button
              className='butt'
              style={{
                height: 35,
                width: 100,
                borderRadius: 20,
              }}
              onClick={() => history.push(ROUTES.SIGNUP)}
            >
              <span style={{ fontSize: 14 }}>S'inscrire</span>
            </Button> */}
          </NavLink>
        </>
      )}
      <div className='appearance-controls' aria-label={t('appearance')}>
        <button
          type='button'
          className='theme-switch'
          aria-label={t('theme')}
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
        >
          {theme === 'light' ? <FiSun /> : <FiMoon />}
          <span>{theme === 'light' ? t('lightTheme') : t('darkTheme')}</span>
        </button>
        <div className='language-switch' aria-label={t('language')}>
          <FiGlobe className='globe' />
          {(['fr', 'en'] as AppLanguage[]).map((item) => (
            <button
              key={item}
              type='button'
              className={language === item ? 'active' : ''}
              onClick={() => setLanguage(item)}
            >
              {item.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SubHeader = () => {
  const location = useLocation();
  const history = useHistory();

  const activeParent = Object.keys(SUB_MENUS).find((parentRoute) => {
    const subMenus = SUB_MENUS[parentRoute];
    return subMenus.some((item) => {
      if (item.path && location.pathname.startsWith(item.path)) return true;
      if (item.dropdown) {
        return item.children.some((child) =>
          location.pathname.startsWith(child.path),
        );
      }
      return false;
    });
  });

  if (!activeParent) return null;

  const currentSub = SUB_MENUS[activeParent];

  return (
    <SubHeaderContainer>
      {currentSub.map((item) =>
        item.dropdown ? (
          <Dropdown
            key={item.key}
            menu={{
              items: item.children.map((child) => ({
                key: child.key,
                label: child.label,
                onClick: () => history.push(child.path),
              })),
            }}
          >
            <div className='sub-link'>{item.label}</div>
          </Dropdown>
        ) : (
          <div
            key={item.key}
            className={`sub-link ${
              location.pathname === item.path ? 'active' : ''
            }`}
            onClick={() => {
              if (item.path) {
                history.push(item.path);
              }
            }}
          >
            {item.label}
          </div>
        ),
      )}
    </SubHeaderContainer>
  );
};

export const Header = ({ other }: { other?: ReactNode }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <>
      <HeaderContainer>
        <Space className='logo'>
          <Image
            alt='moranje-logo'
            src='/assets/images/moranje-logo.png'
            height={40}
            width={40}
            preview={false}
            style={{
              borderRadius: '50%',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.18)',
              cursor: 'pointer',
              objectFit: 'cover',
            }}
            fallback={defaultImage}
            // onClick={() => history.push(ROUTES.HOME_PAGE)}
          />
          <h3 className='brand-title'>
            <span style={{ color: ORANGE }}>Moranj</span>
            <span style={{ color: '#ffffff' }}>E</span>
            <span style={{ color: YELLOW }}>Trade</span>
          </h3>
        </Space>
        <MenuContent other={other} />

        <div className={showMenu ? 'menu' : 'menu-close'}>
          <AiOutlineClose
            size={30}
            color='var(--mt-header-text)'
            style={{ cursor: 'pointer' }}
            onClick={() => setShowMenu(false)}
          />
          <MenuContent closeMenu={() => setShowMenu(false)} other={other} />
        </div>
        <div className='menu-button' onClick={() => setShowMenu(true)}>
          <FaOutdent size={30} color='var(--mt-header-text)' />
        </div>
      </HeaderContainer>
      <SubHeader />
    </>
  );
};
